import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, User, MapPin, Building2, Calendar,
  FileText, CreditCard, Euro, Clock, CheckCircle, AlertTriangle,
  Edit3, Download, Upload, Users, ExternalLink, Receipt
} from 'lucide-react';
import {
  getAffaireById, getClientById, getIngenieurById, getSocieteById, getAgenceById,
  getFacturesByAffaire, getDevisById, sousTraitants,
  statutAffaireLabels, statutFactureLabels, typeFactureLabels
} from '../data/mockData';

const AffaireDetail = () => {
  const { id } = useParams();
  const affaire = getAffaireById(parseInt(id));
  const [activeTab, setActiveTab] = useState('resume');

  if (!affaire) {
    return (
      <div className="page-container">
        <Link to="/affaires" className="btn-back"><ArrowLeft size={18} /> Retour aux affaires</Link>
        <div className="empty-state">
          <Briefcase size={48} />
          <h3>Affaire introuvable</h3>
        </div>
      </div>
    );
  }

  const client = getClientById(affaire.clientId);
  const ingenieur = getIngenieurById(affaire.ingenieurId);
  const societe = getSocieteById(affaire.societeId);
  const agence = getAgenceById(affaire.agenceId);
  const facturesAffaire = getFacturesByAffaire(affaire.id);
  const devisOrigine = affaire.devisId ? getDevisById(affaire.devisId) : null;
  const statut = statutAffaireLabels[affaire.statut];

  const stExterne = sousTraitants.filter(st =>
    st.affaires.some(a => a.affaireId === affaire.id)
  );

  const formatMontant = (montant) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(montant);
  };

  const montantFacture = facturesAffaire
    .filter(f => f.statut === 'payee')
    .reduce((acc, f) => acc + f.montantHT, 0);

  const tabs = [
    { key: 'resume', label: 'Résumé', icon: Briefcase },
    { key: 'facturation', label: 'Facturation', icon: CreditCard, count: facturesAffaire.length },
    { key: 'soustraitance', label: 'Sous-traitance', icon: Users, count: stExterne.length },
    { key: 'documents', label: 'Documents', icon: FileText },
  ];

  return (
    <div className="page-container">
      <Link to="/affaires" className="btn-back"><ArrowLeft size={18} /> Retour aux affaires</Link>

      {/* En-tête */}
      <div className="detail-header">
        <div className="detail-title-row">
          <div className="detail-title">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
              <span className="offre-reference-large">{affaire.numero}</span>
              <span className="statut-badge statut-badge-lg" style={{ background: statut.bg, color: statut.color }}>
                {statut.label}
              </span>
              <span className="badge badge-secondary">{affaire.codeMission}</span>
              <span className="badge" style={{
                background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
              }}>
                <Building2 size={12} style={{ marginRight: '0.25rem' }} />{societe?.nom}
              </span>
              {agence && (
                <span className="badge badge-secondary" style={{ fontSize: '0.6875rem' }}>
                  Agence {agence.nom}
                </span>
              )}
            </div>
            <h1 style={{ fontSize: '1.5rem' }}>{affaire.designation}</h1>
            <div className="detail-meta">
              <span className="meta-item"><MapPin size={16} /> {affaire.adresse}</span>
              <span className="meta-item"><Calendar size={16} /> Créée le {new Date(affaire.dateCreation).toLocaleDateString('fr-FR')}</span>
            </div>
          </div>
          <div className="detail-actions">
            <button className="btn btn-secondary"><Edit3 size={16} /> Modifier</button>
            <Link to="/factures/nouvelle" className="btn btn-primary"><Receipt size={16} /> Créer une facture</Link>
          </div>
        </div>
      </div>

      {/* KPIs affaire */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', marginBottom: '1.5rem' }}>
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Euro size={20} /></div>
          <div className="stat-content">
            <p className="stat-label">Honoraires</p>
            <p className="stat-value" style={{ fontSize: '1.25rem' }}>{formatMontant(affaire.montantHT)}</p>
          </div>
        </div>
        <div className="stat-card stat-success">
          <div className="stat-icon"><CheckCircle size={20} /></div>
          <div className="stat-content">
            <p className="stat-label">Facturé & payé</p>
            <p className="stat-value" style={{ fontSize: '1.25rem' }}>{formatMontant(montantFacture)}</p>
          </div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-icon"><Clock size={20} /></div>
          <div className="stat-content">
            <p className="stat-label">Reste à facturer</p>
            <p className="stat-value" style={{ fontSize: '1.25rem' }}>{formatMontant(affaire.montantHT - montantFacture)}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#EDE9FE', color: '#7C3AED' }}><Users size={20} /></div>
          <div className="stat-content">
            <p className="stat-label">Sous-traitance</p>
            <p className="stat-value" style={{ fontSize: '1.25rem' }}>{formatMontant(affaire.montantSousTraitanceExterne + affaire.montantSousTraitanceInterne)}</p>
          </div>
        </div>
        <div className="stat-card stat-info">
          <div className="stat-icon"><Briefcase size={20} /></div>
          <div className="stat-content">
            <p className="stat-label">Avancement</p>
            <p className="stat-value" style={{ fontSize: '1.25rem' }}>{affaire.avancement}%</p>
          </div>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="progress-bar" style={{ height: '8px', marginBottom: '1.5rem' }}>
        <div className="progress-fill" style={{ width: `${affaire.avancement}%` }}></div>
      </div>

      {/* Onglets */}
      <div className="tabs-container">
        <div className="tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                className={`tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                <Icon size={16} />
                {tab.label}
                {tab.count !== undefined && (
                  <span className="tab-count">{tab.count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tab-content">
        {/* Résumé */}
        {activeTab === 'resume' && (
          <div className="resume-grid">
            <div className="resume-card">
              <h3>Informations générales</h3>
              <div className="info-list">
                <div className="info-row"><span className="info-label">N° d'affaire</span><span className="info-value">{affaire.numero}</span></div>
                <div className="info-row"><span className="info-label">Code mission</span><span className="info-value">{affaire.codeMission}</span></div>
                <div className="info-row">
                  <span className="info-label">Société</span>
                  <span className="info-value">
                    <span className="badge" style={{
                      background: societe?.id === 1 ? '#DBEAFE' : '#FFF7ED',
                      color: societe?.id === 1 ? '#1E40AF' : '#9A3412',
                    }}>{societe?.nom}</span>
                  </span>
                </div>
                <div className="info-row"><span className="info-label">Agence</span><span className="info-value">{agence?.nom || '-'}</span></div>
                <div className="info-row"><span className="info-label">Date début</span><span className="info-value">{new Date(affaire.dateDebut).toLocaleDateString('fr-FR')}</span></div>
                {affaire.dateFin && <div className="info-row"><span className="info-label">Date fin</span><span className="info-value">{new Date(affaire.dateFin).toLocaleDateString('fr-FR')}</span></div>}
                {devisOrigine && (
                  <div className="info-row">
                    <span className="info-label">Devis d'origine</span>
                    <Link to={`/devis/${devisOrigine.id}`} className="info-value" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                      {devisOrigine.numero} <ExternalLink size={12} />
                    </Link>
                  </div>
                )}
              </div>
            </div>

            <div className="resume-card">
              <h3>Client</h3>
              <div className="info-list">
                <div className="info-row">
                  <span className="info-label">Nom</span>
                  <Link to={`/clients/${client?.id}`} className="info-value" style={{ color: 'var(--primary)', textDecoration: 'none' }}>
                    {client?.nom} <ExternalLink size={12} />
                  </Link>
                </div>
                <div className="info-row"><span className="info-label">Type</span><span className="info-value">{client?.marchePublic ? 'Marché public' : 'Client privé'}</span></div>
                <div className="info-row"><span className="info-label">Contact technique</span><span className="info-value">{client?.contactTechnique.nom}</span></div>
                <div className="info-row"><span className="info-label">Téléphone</span><span className="info-value">{client?.contactTechnique.telephone}</span></div>
                <div className="info-row"><span className="info-label">Email</span><span className="info-value">{client?.contactTechnique.email}</span></div>
              </div>
            </div>

            <div className="resume-card">
              <h3>Ingénieur en charge</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                <div className="user-avatar" style={{ background: ingenieur?.couleur }}>{ingenieur?.initiales}</div>
                <div>
                  <p style={{ fontWeight: 600, margin: 0 }}>{ingenieur?.prenom} {ingenieur?.nom}</p>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: 0 }}>{ingenieur?.role}</p>
                </div>
              </div>
              <div className="info-list">
                <div className="info-row"><span className="info-label">Email</span><span className="info-value">{ingenieur?.email}</span></div>
                <div className="info-row"><span className="info-label">Téléphone</span><span className="info-value">{ingenieur?.telephone}</span></div>
              </div>
            </div>

            <div className="resume-card">
              <h3>Financier</h3>
              <div className="info-list">
                <div className="info-row"><span className="info-label">Montant total HT</span><span className="info-value" style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatMontant(affaire.montantHT)}</span></div>
                <div className="info-row"><span className="info-label">Sous-traitance externe</span><span className="info-value">{formatMontant(affaire.montantSousTraitanceExterne)}</span></div>
                <div className="info-row"><span className="info-label">Sous-traitance interne</span><span className="info-value">{formatMontant(affaire.montantSousTraitanceInterne)}</span></div>
                <div className="info-row" style={{ paddingTop: '0.5rem', borderTop: '1px solid var(--gray-200)' }}>
                  <span className="info-label" style={{ fontWeight: 600 }}>Production propre</span>
                  <span className="info-value" style={{ fontWeight: 700, color: 'var(--success)' }}>
                    {formatMontant(affaire.montantHT - affaire.montantSousTraitanceExterne - affaire.montantSousTraitanceInterne)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Facturation */}
        {activeTab === 'facturation' && (
          <div>
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Calendar size={18} /> Échéancier de facturation
            </h3>
            <div style={{ overflowX: 'auto', marginBottom: '2rem' }}>
              <table className="factures-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Échéance</th>
                    <th>Mois prévu</th>
                    <th>Montant HT</th>
                    <th>Statut</th>
                    <th>Facture</th>
                  </tr>
                </thead>
                <tbody>
                  {affaire.echeances.map((ech, index) => {
                    const facture = facturesAffaire.find(f => f.id === ech.factureId);
                    const getEchStatutStyle = () => {
                      switch (ech.statut) {
                        case 'paye': return { bg: '#D1FAE5', color: '#059669', label: 'Payé' };
                        case 'facture': return { bg: '#DBEAFE', color: '#2563EB', label: 'Facturé' };
                        case 'en_retard': return { bg: '#FEE2E2', color: '#DC2626', label: 'En retard' };
                        default: return { bg: '#FEF3C7', color: '#D97706', label: 'À facturer' };
                      }
                    };
                    const echStatut = getEchStatutStyle();
                    return (
                      <tr key={index}>
                        <td style={{ fontWeight: 500 }}>{ech.label}</td>
                        <td>{ech.moisPrevu ? new Date(ech.moisPrevu + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : '-'}</td>
                        <td style={{ fontWeight: 600 }}>{formatMontant(ech.montant)}</td>
                        <td>
                          <span className="statut-badge" style={{ background: echStatut.bg, color: echStatut.color }}>
                            {echStatut.label}
                          </span>
                        </td>
                        <td>
                          {facture ? (
                            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8125rem', color: 'var(--primary)' }}>
                              {facture.numero}
                            </span>
                          ) : (
                            <Link to="/factures/nouvelle" className="btn btn-xs btn-primary">
                              Créer facture
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Receipt size={18} /> Factures émises
            </h3>
            {facturesAffaire.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table className="factures-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>N° Facture</th>
                      <th>Type</th>
                      <th>Date envoi</th>
                      <th>Montant HT</th>
                      <th>Montant TTC</th>
                      <th>Statut</th>
                      <th>Paiement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturesAffaire.map(f => {
                      const fStatut = statutFactureLabels[f.statut];
                      const fType = typeFactureLabels[f.type];
                      return (
                        <tr key={f.id}>
                          <td><span className="facture-numero-cell">{f.numero}</span></td>
                          <td><span className="statut-badge" style={{ background: fType.bg, color: fType.color }}>{fType.label}</span></td>
                          <td>{f.dateEnvoi ? new Date(f.dateEnvoi).toLocaleDateString('fr-FR') : '-'}</td>
                          <td style={{ fontWeight: 600 }}>{formatMontant(f.montantHT)}</td>
                          <td>{formatMontant(f.montantTTC)}</td>
                          <td><span className="statut-badge" style={{ background: fStatut.bg, color: fStatut.color }}>{fStatut.label}</span></td>
                          <td>{f.datePaiement ? new Date(f.datePaiement).toLocaleDateString('fr-FR') : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <CreditCard size={36} />
                <h3>Aucune facture émise</h3>
                <Link to="/factures/nouvelle" className="btn btn-primary" style={{ marginTop: '0.5rem' }}>
                  Créer la première facture
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Sous-traitance */}
        {activeTab === 'soustraitance' && (
          <div>
            {affaire.montantSousTraitanceExterne > 0 && (
              <>
                <h3 style={{ marginBottom: '1rem' }}>Sous-traitance externe</h3>
                {stExterne.map(st => {
                  const affaireST = st.affaires.find(a => a.affaireId === affaire.id);
                  return (
                    <div key={st.id} style={{
                      padding: '1.25rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)',
                      marginBottom: '0.75rem', border: '1px solid var(--gray-200)'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div>
                          <h4 style={{ margin: '0 0 0.25rem 0' }}>{st.nom}</h4>
                          <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', margin: 0 }}>{st.specialite}</p>
                          <p style={{ fontSize: '0.8125rem', color: 'var(--gray-600)', margin: '0.25rem 0 0 0' }}>
                            Contact : {st.contact} — {st.telephone}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, color: 'var(--primary)' }}>{formatMontant(affaireST.montantDevis)}</div>
                          <span className={`badge ${st.documentsValides ? 'badge-success' : 'badge-danger'}`}>
                            {st.documentsValides ? 'Documents OK' : 'Documents manquants'}
                          </span>
                        </div>
                      </div>
                      {affaireST.factureNumero && (
                        <div style={{ marginTop: '0.75rem', padding: '0.5rem', background: 'white', borderRadius: 'var(--radius-sm)' }}>
                          <span style={{ fontSize: '0.8125rem', color: 'var(--gray-600)' }}>
                            Facture : <strong>{affaireST.factureNumero}</strong> — {formatMontant(affaireST.montantTTC)} TTC
                            {affaireST.datePaiement
                              ? <span className="badge badge-success" style={{ marginLeft: '0.5rem' }}>Payé le {new Date(affaireST.datePaiement).toLocaleDateString('fr-FR')}</span>
                              : <span className="badge badge-warning" style={{ marginLeft: '0.5rem' }}>En attente de paiement</span>
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}

            {affaire.montantSousTraitanceInterne > 0 && (
              <>
                <h3 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Sous-traitance interne</h3>
                <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)' }}>
                  <p style={{ margin: 0, color: 'var(--gray-600)' }}>
                    Montant sous-traitance interne : <strong>{formatMontant(affaire.montantSousTraitanceInterne)}</strong>
                  </p>
                </div>
              </>
            )}

            {affaire.montantSousTraitanceExterne === 0 && affaire.montantSousTraitanceInterne === 0 && (
              <div className="empty-state" style={{ padding: '2rem' }}>
                <Users size={36} />
                <h3>Pas de sous-traitance</h3>
                <p>Cette affaire ne comporte pas de sous-traitance.</p>
              </div>
            )}
          </div>
        )}

        {/* Documents */}
        {activeTab === 'documents' && (
          <div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {devisOrigine && (
                <Link to={`/devis/${devisOrigine.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)', cursor: 'pointer' }}>
                    <FileText size={20} style={{ color: 'var(--primary)' }} />
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: 0, fontWeight: 600 }}>Devis d'origine — {devisOrigine.numero}</p>
                      <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-500)' }}>Créé le {new Date(devisOrigine.dateCreation).toLocaleDateString('fr-FR')} — Importé depuis TIIME</p>
                    </div>
                    <button className="btn btn-sm btn-secondary"><Download size={14} /> Télécharger</button>
                  </div>
                </Link>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)' }}>
                <FileText size={20} style={{ color: 'var(--success)' }} />
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>Devis signé / Bon de commande</p>
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-500)' }}>Document validé par le client</p>
                </div>
                <button className="btn btn-sm btn-secondary"><Upload size={14} /> Importer</button>
              </div>
              {facturesAffaire.map(f => (
                <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)' }}>
                  <Receipt size={20} style={{ color: 'var(--accent)' }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 600 }}>Facture {f.numero}</p>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: 'var(--gray-500)' }}>
                      {typeFactureLabels[f.type].label} — {formatMontant(f.montantTTC)} TTC
                    </p>
                  </div>
                  <button className="btn btn-sm btn-secondary"><Download size={14} /> Télécharger</button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffaireDetail;
